import React, { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { Mail, Lock, User, Loader2, Eye, EyeOff } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { useToast } from "../hooks/use-toast";

export default function AuthModal({ open, onOpenChange, defaultTab = "login" }) {
  const [tab, setTab] = useState(defaultTab);
  const { login, register } = useAuth();
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [showPwd, setShowPwd] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", password: "" });

  React.useEffect(() => { if (open) setTab(defaultTab); }, [open, defaultTab]);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (tab === "login") {
        await login(form.email, form.password);
        toast({ title: "Bienvenido de nuevo", description: "Has iniciado sesi\u00f3n correctamente." });
      } else {
        if (form.name.trim().length < 2) throw new Error("Ingresa tu nombre");
        await register(form.name, form.email, form.password);
        toast({ title: "¡Cuenta creada!", description: "Bienvenido a Números Fácil." });
      }
      onOpenChange(false);
      setForm({ name: "", email: "", password: "" });
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.message || "Error desconocido";
      toast({ title: tab === "login" ? "No se pudo iniciar sesi\u00f3n" : "No se pudo crear la cuenta", description: msg });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md p-0 overflow-hidden">
        <div className="bg-gradient-to-br from-[#0e4a7a] via-[#1d6cb1] to-[#2a83ce] p-6 text-white">
          <DialogHeader>
            <DialogTitle className="text-2xl font-extrabold text-white">
              {tab === "login" ? "Iniciar sesi\u00f3n" : "Crear cuenta"}
            </DialogTitle>
            <DialogDescription className="text-blue-100">
              {tab === "login" ? "Accede a tus n\u00fameros VIP y favoritos." : "Reg\u00edstrate gratis y desbloquea funciones exclusivas."}
            </DialogDescription>
          </DialogHeader>
        </div>

        <div className="px-6 pb-6 pt-4">
          <div className="flex bg-blue-50 rounded-full p-1 mb-5">
            <button
              type="button"
              onClick={() => setTab("login")}
              className={`flex-1 py-2 text-sm font-semibold rounded-full transition ${tab === "login" ? "bg-white text-[#0e4a7a] shadow" : "text-gray-600"}`}
            >
              Iniciar sesión
            </button>
            <button
              type="button"
              onClick={() => setTab("register")}
              className={`flex-1 py-2 text-sm font-semibold rounded-full transition ${tab === "register" ? "bg-white text-[#0e4a7a] shadow" : "text-gray-600"}`}
            >
              Crear cuenta
            </button>
          </div>

          <form onSubmit={submit} className="space-y-3">
            {tab === "register" && (
              <label className="block">
                <span className="text-xs font-semibold text-gray-700">Nombre</span>
                <div className="mt-1 flex items-center bg-blue-50/60 border-2 border-blue-100 focus-within:border-[#1d6cb1] rounded-xl px-3 py-2 transition">
                  <User className="w-4 h-4 text-[#1d6cb1] mr-2" />
                  <input
                    type="text"
                    value={form.name}
                    onChange={update("name")}
                    placeholder="Tu nombre"
                    className="flex-1 bg-transparent outline-none text-sm"
                    required
                  />
                </div>
              </label>
            )}
            <label className="block">
              <span className="text-xs font-semibold text-gray-700">Correo electrónico</span>
              <div className="mt-1 flex items-center bg-blue-50/60 border-2 border-blue-100 focus-within:border-[#1d6cb1] rounded-xl px-3 py-2 transition">
                <Mail className="w-4 h-4 text-[#1d6cb1] mr-2" />
                <input
                  type="email"
                  value={form.email}
                  onChange={update("email")}
                  placeholder="tu@correo.com"
                  className="flex-1 bg-transparent outline-none text-sm"
                  required
                />
              </div>
            </label>
            <label className="block">
              <span className="text-xs font-semibold text-gray-700">Contraseña</span>
              <div className="mt-1 flex items-center bg-blue-50/60 border-2 border-blue-100 focus-within:border-[#1d6cb1] rounded-xl px-3 py-2 transition">
                <Lock className="w-4 h-4 text-[#1d6cb1] mr-2" />
                <input
                  type={showPwd ? "text" : "password"}
                  value={form.password}
                  onChange={update("password")}
                  placeholder={tab === "login" ? "Tu contrase\u00f1a" : "M\u00ednimo 6 caracteres"}
                  className="flex-1 bg-transparent outline-none text-sm"
                  minLength={6}
                  required
                />
                <button type="button" onClick={() => setShowPwd((v) => !v)} className="text-gray-400 hover:text-gray-600 p-1" tabIndex={-1}>
                  {showPwd ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </label>

            <button
              type="submit"
              disabled={loading}
              className="w-full mt-2 inline-flex items-center justify-center gap-2 px-5 py-3 rounded-xl bg-gradient-to-r from-[#1d6cb1] to-[#2a83ce] text-white font-semibold shadow hover:shadow-lg disabled:opacity-60 transition"
            >
              {loading && <Loader2 className="w-4 h-4 animate-spin" />}
              {tab === "login" ? "Iniciar sesi\u00f3n" : "Crear cuenta"}
            </button>

            <p className="text-center text-xs text-gray-500 mt-3">
              {tab === "login" ? (
                <>¿No tienes cuenta?{" "}
                  <button type="button" onClick={() => setTab("register")} className="text-[#1d6cb1] font-semibold hover:underline">
                    Regístrate gratis
                  </button>
                </>
              ) : (
                <>¿Ya tienes cuenta?{" "}
                  <button type="button" onClick={() => setTab("login")} className="text-[#1d6cb1] font-semibold hover:underline">
                    Inicia sesión
                  </button>
                </>
              )}
            </p>
          </form>
        </div>
      </DialogContent>
    </Dialog>
  );
}
